// MaturaAbsoluteValueEquationClosedTask.js
// Zadanie maturalne zamknięte: równanie |x - a| = b,
// pytamy o iloczyn LUB sumę rozwiązań.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const sign = (v) => (v > 0 ? 1 : v < 0 ? -1 : 0);

// ============================================================================
// GENERATOR
// ============================================================================

function createJune2025OriginalTask() {
  const options = [4, 20, 24, 100].map((value, index) => ({
    label: LETTERS[index],
    value,
    isCorrect: value === 20,
  }));

  return {
    type: "MaturaAbsoluteValueEquationClosedTask",
    data: {
      a: 6,
      b: 4,
      x1: 2,
      x2: 10,
      sum: 12,
      product: 20,
      questionType: "product",
      correctValue: 20,
      options,
      correctIndex: 1,
      originalCke: true,
    },
    tags: ["wartość_bezwzględna", "równanie"],
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  let a, b, x1, x2, product;

  // unikamy iloczynu = 0, żeby znak nie był trywialny
  do {
    a = randInt(1, 10);
    b = randInt(1, 10);
    x1 = a - b;
    x2 = a + b;
    product = a * a - b * b;
  } while (product === 0);

  const sum = x1 + x2; // = 2a

  const forcedQuestionType = ["sum", "product"].includes(generationOptions.questionType)
    ? generationOptions.questionType
    : Number.isInteger(generationOptions.courseSlot)
      ? ["sum", "product"][generationOptions.courseSlot % 2]
      : null;
  const askSum = forcedQuestionType
    ? forcedQuestionType === "sum"
    : Math.random() < 0.5;
  const questionType = askSum ? "sum" : "product";
  const correctValue = askSum ? sum : product;

  // --- kandydaci na błędne odpowiedzi ---
  let candidateWrong;
  if (askSum) {
    // poprawna: x1 + x2 = 2a
    // typowe "pomyłki": iloczyn, 2b, a+b, a-b, -sum
    candidateWrong = [product, 2 * b, a + b, a - b, -sum];
  } else {
    // poprawna: x1 * x2 = a^2 - b^2
    // typowe pomyłki: suma, a*b, b^2 - a^2, -product, 2ab
    candidateWrong = [sum, a * b, b * b - a * a, -product, 2 * a * b];
  }

  // wyrzucamy powtórki i poprawną wartość
  const uniqueWrong = [];
  const seenWrong = new Set();
  seenWrong.add(correctValue);
  for (const v of candidateWrong) {
    if (!seenWrong.has(v)) {
      seenWrong.add(v);
      uniqueWrong.push(v);
    }
  }

  // wybieramy maks. 3 błędne odpowiedzi + ewentualne do-sztuczne
  const wrongs = [];
  for (const v of shuffle(uniqueWrong)) {
    if (wrongs.length >= 3) break;
    wrongs.push(v);
  }

  // jakby było mniej niż 3 – dokładamy sztuczne w pobliżu poprawnej
  while (wrongs.length < 3) {
    const dir = sign(correctValue) || 1;
    const tweak = correctValue + dir * randInt(1, 3);
    if (!wrongs.includes(tweak) && tweak !== correctValue) {
      wrongs.push(tweak);
    }
  }

  // ⬇⬇ BEZPIECZNIK 1: przynajmniej jedna błędna odpowiedź
  // ma ten sam znak co poprawna (żeby nie strzelać po plus/minus)
  if (!wrongs.some((w) => sign(w) === sign(correctValue))) {
    const dir = sign(correctValue) || 1;
    let tweak = correctValue + dir * randInt(1, 3);
    if (wrongs.includes(tweak) || tweak === correctValue) {
      tweak = correctValue + dir * 4;
    }
    wrongs[0] = tweak;
  }

  // ⬇⬇ BEZPIECZNIK 2: jeśli istnieje para (correctValue, -correctValue),
  // to dokładamy JESZCZE jedną parę k i -k wśród błędnych odpowiedzi,
  // żeby ta „z przeciwnym znakiem” nie była jedynym takim przypadkiem.
  if (wrongs.includes(-correctValue)) {
    const idxOpp = wrongs.indexOf(-correctValue);
    const otherIdx = [0, 1, 2].filter((i) => i !== idxOpp);
    const [j, k] = otherIdx;

    // wybieramy nowe m tak, żeby |m| != |correctValue|
    let m = Math.abs(correctValue) + 1;
    if (m === 0 || m === Math.abs(correctValue)) m += 1;

    wrongs[j] = m;
    wrongs[k] = -m;
  }

  // składamy pełną listę opcji (1 poprawna + 3 błędne)
  let rawOptions = [
    { value: correctValue, isCorrect: true },
    ...wrongs.map((v) => ({ value: v, isCorrect: false })),
  ];

  // ostateczne od-duplikowanie wartości (na wszelki wypadek)
  const seenFinal = new Set();
  rawOptions = rawOptions.map((o) => {
    let v = o.value;
    while (seenFinal.has(v)) v += 1;
    seenFinal.add(v);
    return { ...o, value: v };
  });

  const shuffled = shuffle(rawOptions).map((o, idx) => ({
    label: LETTERS[idx],
    value: o.value,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaAbsoluteValueEquationClosedTask",
    data: {
      a,
      b,
      x1,
      x2,
      sum,
      product,
      questionType, // "sum" albo "product"
      correctValue,
      options: shuffled,
      correctIndex,
    },
    tags: ["wartość_bezwzględna", "równanie"],
  };
}

// ============================================================================
// RENDER TASK
// ============================================================================

export function renderTask({ data }) {
  const { a, b, options, questionType } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Liczby <InlineMath math={"x_1"} /> i{" "}
        <InlineMath math={"x_2"} /> są różnymi rozwiązaniami równania{" "}
        <InlineMath math={`|x - ${a}| = ${b}`} />.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        {questionType === "product" ? (
          <>
            Iloczyn <InlineMath math={"x_1 \\cdot x_2"} /> jest równy:
          </>
        ) : (
          <>
            Suma <InlineMath math={"x_1 + x_2"} /> jest równa:
          </>
        )}
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// INPUT
// ============================================================================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={options.map((o, idx) => ({
        value: idx,
        label: o.label,
        textNode: o.label,
      }))}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ============================================================================
// CHECK ANSWER
// ============================================================================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

// ============================================================================
// RENDER ANSWER
// ============================================================================

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.value}`} />;
}

// ============================================================================
// RENDER USER ANSWER
// ============================================================================

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.value}`} />
    </div>
  );
}

// ============================================================================
// WYJAŚNIENIE
// ============================================================================

export function buildExplanationEquations({ a, b, x1, x2 }) {
  return {
    eq1: `x - ${a} = ${b}`,
    eq1b: `x = ${a} + ${b}`,
    eq1c: `x = ${x2}`,
    eq2: `x - ${a} = -${b}`,
    eq2b: `x = ${a} - ${b}`,
    eq2c: `x = ${x1}`,
  };
}

export function renderExplanation({ data }) {
  const { a, b, x1, x2, sum, product, questionType, correctValue } = data;

  const { eq1, eq1b, eq1c, eq2, eq2b, eq2c } =
    buildExplanationEquations(data);

  const opWord = questionType === "product" ? "iloczyn" : "suma";
  const opSymbol =
    questionType === "product"
      ? `${x1} \\cdot ${x2} = ${product}`
      : `${x1} + ${x2} = ${sum}`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Rozpisujemy wartość bezwzględną na dwa
        równania.
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={`|x - ${a}| = ${b}`} />
        <BlockMath
          math={`x - ${a} = ${b}\\;\\text{ lub }\\; x - ${a} = -${b}`}
        />
      </div>

      <p>
        <strong>Krok 2.</strong> Rozwiązujemy oba równania osobno.
      </p>

      <p>
        <em>Pierwszy przypadek:</em>
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={eq1} />
        <BlockMath math={eq1b} />
        <BlockMath math={eq1c} />
      </div>

      <p>
        <em>Drugi przypadek:</em>
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={eq2} />
        <BlockMath math={eq2b} />
        <BlockMath math={eq2c} />
      </div>

      <p>
        <strong>Krok 3.</strong> Liczymy {opWord} obu rozwiązań.
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={opSymbol} />
      </div>

      <p>
        <strong>Wniosek:</strong> poprawna odpowiedź to{" "}
        <InlineMath math={`${correctValue}`} />.
      </p>
    </div>
  );
}
